import React from 'react';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Minus from 'lucide-react/dist/esm/icons/minus.js';
import Plus from 'lucide-react/dist/esm/icons/plus.js';
import { usePurchasePage } from './PurchasePageContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';

const MIN_WEEKS = 1;
const MAX_WEEKS = 52;

/**
 * Multi-week selector used by Modified 50/50 purchases.
 * Self-hides for all non-Modified game types so the section can sit in the
 * default purchase sections list without leaking into other games.
 */
export default function PurchaseWeeksSelectorSection({ section, config }) {
  const { gameType, numWeeks, setNumWeeks } = usePurchasePage();
  const { c } = useContent();
  const { t: tr } = useTranslations();

  if (gameType !== 'modified5050') return null;

  const max = Math.max(MIN_WEEKS, Math.min(MAX_WEEKS, config?.maxWeeks ?? MAX_WEEKS));
  const value = Math.max(MIN_WEEKS, Math.min(max, Number(numWeeks) || MIN_WEEKS));

  const decrement = () => setNumWeeks(Math.max(MIN_WEEKS, value - 1));
  const increment = () => setNumWeeks(Math.min(max, value + 1));

  const weeksKey = value === 1 ? 'purchase.weeks_selector.weekSingular' : 'purchase.weeks_selector.weekPlural';
  const weeksFallback = value === 1 ? 'Week' : 'Weeks';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-lg mb-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-charity-primary" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            <InlineTranslationEdit
              translationKey="purchase.weeks_selector.title"
              value={c('purchase.weeks_selector.title', tr('purchase.weeks_selector.title') || 'Number of Weeks')}
              defaultText="Number of Weeks"
            />
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={decrement}
            disabled={value <= MIN_WEEKS}
            aria-label={c('purchase.weeks_selector.decrease', tr('purchase.weeks_selector.decrease') || 'Decrease')}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>

          <div className="flex items-baseline gap-1.5 min-w-[4rem] justify-center">
            <span className="text-2xl font-black text-charity-primary leading-none">
              {value}
            </span>
            <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <InlineTranslationEdit
                translationKey={weeksKey}
                value={c(weeksKey, weeksFallback)}
                defaultText={weeksFallback}
              />
            </span>
          </div>

          <button
            type="button"
            onClick={increment}
            disabled={value >= max}
            aria-label={c('purchase.weeks_selector.increase', tr('purchase.weeks_selector.increase') || 'Increase')}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-charity-primary text-white hover:bg-charity-primary-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        <InlineTranslationEdit
          translationKey="purchase.weeks_selector.min"
          value={c('purchase.weeks_selector.min', tr('purchase.weeks_selector.min') || 'Min')}
          defaultText="Min"
        /> {MIN_WEEKS} · <InlineTranslationEdit
          translationKey="purchase.weeks_selector.max"
          value={c('purchase.weeks_selector.max', tr('purchase.weeks_selector.max') || 'Max')}
          defaultText="Max"
        /> {max}
      </p>
    </div>
  );
}
